import {observable, action, observe, computed} from 'mobx';
import {DomainStore} from 'shared/store';
import {types, endpoints, api, t, auth} from 'shared/core';
import {TimeOffRequest, ForecastedAccountBalance, TimeOffPolicy} from 'stores/time_off';
import DayOffViewModel from 'stores/time_off/DayOffViewModel';
import _ from 'lodash';
import {successAlert, rangeDate, dateToJson} from 'shared/tools';
import moment from 'moment';

class RequestTimeOffContainerState {
  store = new DomainStore();
  history;

  @observable employee;
  @observable types = [];
  @observable typePolicies = [];
  @observable request = new TimeOffRequest();
  @observable errors = {};
  @observable forecastingAccount = false;
  @observable forecastedBalance;
  @observable fetchingRequestedDays = false;
  @observable daysOffJson = [];
  @observable specifyCustomHours = false;
  @observable dayOffModalOpen = false;
  @observable editDayOff = null;
  @observable delegateUsers;
  @observable conflictingDates = [];
  @observable allowedToDelegate;

  receiveProps({employee, history}) {
    this.employee = employee;
    this.history = history;
  }

  @action async load() {
    const composed = await this.store._compose([
      endpoints.TIME_OFF.POLICY.withEmployeeId(this.employee.id),
      endpoints.USERS.ACTIVE
    ]);

    const policy = new TimeOffPolicy(composed[0].data);
    this.types = _.map(policy.typePolicies, 'type');
    this.typePolicies = policy.typePolicies;

    this.delegateUsers = this.store.getUsers();

    let request = await api.get(endpoints.TIME_OFF.ALLOWED_TO_DELEGATE.with(this.employee.id));
    this.allowedToDelegate = request.data;

    this.initRequest();

    observe(this.request, 'startDate', () => this.forecastAccount());
    observe(this.request, 'startDate', () => this.fetchRequestedDays());
    observe(this.request, 'endDate', () => this.fetchRequestedDays(), true);
    observe(this.request, 'timeOffType', () => this.forecastAccount(), true);
  }

  @action initRequest() {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    this.request.timeOffType = this.sortedTypes[0];
    this.request.merge({
      startDate: today,
      endDate: today
    });
  }

  @action async forecastAccount() {
    if (!this.request.hasValidDates) {
      return;
    }

    this.forecastingAccount = true;

    const response = await api.get(
      endpoints.TIME_OFF.FORECAST_ACCOUNT_BALANCE.with({
        employeeId: this.employee.id,
        typeId: this.request.timeOffType.id,
        date: dateToJson(this.request.startDate)
      })
    );

    this.forecastedBalance = new ForecastedAccountBalance({
      ...response.data.balance,
      workDayLength: response.data.work_day_length,
      unlimited: response.data.unlimited
    });

    this.forecastingAccount = false;
  }

  @action async fetchRequestedDays() {
    this.conflictingDates = [];

    if (!this.request.hasValidDates) {
      return;
    }

    if (this.request.endDate < this.request.startDate) {
      this.request.merge({endDate: this.request.startDate});
    }

    if (moment(this.request.endDate) > moment(this.request.startDate).add(1, 'y').subtract(1, 'd')) {
      this.request.merge({endDate: moment(this.request.startDate).add(1, 'y').subtract(1, 'd').toDate()});
    }

    this.fetchingRequestedDays = true;

    const response = await api.get(
      endpoints.TIME_OFF.DAYS_OFF.with({
        employeeId: this.employee.id,
        typeId: this.request.timeOffType.id,
        startDate: dateToJson(this.request.startDate),
        endDate: dateToJson(this.request.endDate)
      })
    );

    this.daysOffJson = response.data;

    this.fetchingRequestedDays = false;
  }

  @action toggleSpecifyCustomHours() {
    this.specifyCustomHours = !this.specifyCustomHours;
  }

  @action editHoursFor(dayOff) {
    if (!dayOff.canUpdateHours) return;

    this.editDayOff = new DayOffViewModel(_.cloneDeep(dayOff.json));
    this.dayOffModalOpen = true;
  }

  @action closeDayOffModal() {
    this.dayOffModalOpen = false;
  }

  @action updateCustomHours() {
    if (!this.editDayOff.valid) {
      return;
    }

    this.editDayOff.json.cost = Number(this.editDayOff.json.cost);
    const originalDayOff = _.find(this.daysOff, dayOff => dayOff.equalTo(this.editDayOff));
    _.merge(originalDayOff.json, this.editDayOff.json);
    this.closeDayOffModal();
  }

  @action async submitRequest() {
    this.request.daysOff = this.daysOffJson;
    this.request.customHours = this.specifyCustomHours;

    const {model, errors} = await this.store.post(
      endpoints.TIME_OFF.CONFLICTING_REQUESTS.with(this.employee.id),
      types.TIME_OFF.CONFLICT,
      {
        startDate: this.request.startDate,
        endDate: this.request.endDate,
        daysOff: this.daysOffJson,
        typeId: this.request.timeOffType.id
      }
    );
    this.errors = errors;

    if(model) {
      this.conflictingDates = model.dates;

      if (this.conflictingDates.length > 0) {
        return;
      }

      const response = await this.store.post(
        endpoints.TIME_OFF.NEW_TIME_OFF_REQUEST.with({employeeId: this.employee.id}),
        types.TIME_OFF.REQUEST,
        this.request
      );

      this.errors = response.errors;

      if (response.model) {
        this.showSuccessAlert();
        this.history.push(`/${this.employee.id}/time-off`);
      }
    }
  }

  @action changeTimeOffType(timeOffType) {
    this.request.merge({timeOffType});
    this.conflictingDates = [];
  }

  showSuccessAlert() {
    if (moment(this.request.startDate).isSame(this.request.endDate, 'day')) {
      successAlert(
        t('employees.profile.time_off.request.REQUESTED_TIME_OFF_ON_DATE', {
          date: rangeDate(this.request.startDate)
        })
      );
    } else {
      successAlert(
        t('employees.profile.time_off.request.REQUESTED_TIME_OFF', {
          startDate: rangeDate(this.request.startDate),
          endDate: rangeDate(this.request.endDate)
        })
      );
    }
  }

  @computed get usageItems() {
    if (!this.forecastedBalance) return [];

    return [
      {
        title: t('employees.profile.time_off.request.forecast.Available'),
        value: this.forecastedBalance.unlimited ?
          t('employees.profile.time_off.request.forecast.Unlimited') :
          t('employees.profile.time_off.request.forecast.DAYS', {days: this.forecastedBalance.availableDays})
      },
      {
        title: t('employees.profile.time_off.request.forecast.Used'),
        value: t('employees.profile.time_off.request.forecast.DAYS', {days: this.forecastedBalance.usedDays})
      },
      {
        title: t('employees.profile.time_off.request.forecast.Scheduled'),
        value: t('employees.profile.time_off.request.forecast.DAYS', {days: this.forecastedBalance.scheduledDays})
      }
    ];
  }

  @computed get daysOff() {
    return this.daysOffJson.map(json => new DayOffViewModel(json));
  }

  @computed get totalCalendarDays() {
    return moment(this.request.endDate).diff(this.request.startDate, 'days') + 1;
  }

  @computed get totalCostInDays() {
    return _.sumBy(this.daysOff, 'days');
  }

  @computed get totalNonWorkDays() {
    return _.filter(this.daysOff, 'hasCompanyDayOff').length;
  }

  @computed get includedHolidays() {
    return _.filter(this.daysOff, 'hasHoliday').map(d => d.holidayName);
  }

  @computed get exceededHours() {
    return _.chain(this.conflictingDates)
      .map((day) => {
        return {
          date: day.date,
          remainingHours: parseFloat(day.remainingHours),
          requestedHours: this.requestedHours(day.date)
        };
      })
      .filter(day => day.requestedHours > day.remainingHours)
      .value();
  }

  @computed get waitingPeriodFinalDay() {
    return moment(this.employee.startDate).add(this.currentTypePolicy.waitPeriod, 'days');
  }

  @computed get showProbationWarning() {
    if (this.currentTypePolicy.waitPeriod === 0) return false;

    const requestWithinWaitingPeriod = this.waitingPeriodFinalDay.isSameOrAfter(moment(this.request.startDate));

    return requestWithinWaitingPeriod && (auth.hasAccess('::MANAGE_TIME_OFF') || this.employee.accessLevel('::TIME_OFF') === 'w');
  }

  @computed get summaryItems() {
    let items = [
      {
        title: t('employees.profile.time_off.request.summary.Duration'),
        value: t('employees.profile.time_off.request.summary.CALENDAR_DAYS', {days: this.totalCalendarDays})
      }
    ];
    if (this.totalNonWorkDays) {
      items.push({
        title: t('employees.profile.time_off.request.summary.Included Non-work Days'),
        value: t('employees.profile.time_off.request.summary.COST_IN_DAYS', {days: this.totalNonWorkDays})
      });
    }
    if (this.includedHolidays.length) {
      items.push({
        title: t('employees.profile.time_off.request.summary.Included Holidays'),
        value: this.includedHolidays.join(', ')
      });
    }
    items.push({
      title: t('employees.profile.time_off.request.summary.Total Cost'),
      value: t('employees.profile.time_off.request.summary.COST_IN_DAYS', {days: this.totalCostInDays})
    });

    return items;
  }

  @computed get sortedTypes() {
    return _.sortBy(this.types, 'order');
  }

  @computed get currentTypePolicy() {
    return _.find(this.typePolicies, {
      type: { id: this.request.timeOffType.id }
    });
  }

  requestedHours(date) {
    return parseFloat(_.find(this.request.daysOff, dayOff => date === dayOff.date).cost);
  }
}

export default RequestTimeOffContainerState;
